// Speaker-attributed transcript viewer synced to an HTML5 <audio> element: clicking a
// segment seeks the player; playback highlights and auto-scrolls the active segment.
// Also hosts inline segment-text editing and per-speaker rename/merge UI — both optional
// (props default to no-ops) so this component still renders read-only wherever a caller
// doesn't wire them up (e.g. a future read-only share view).
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Segment, Speaker, Transcript, TranscriptWords, WordTiming } from '@scribe/core/types';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Avatar, PlaybackSpeedControl, formatMs, formatSpeaker, speakerColor } from './ui';
import { UserPicker } from './UserPicker';
import { VoicePrintConsent } from './VoicePrintConsent';
import { alignWordsToSegments, findActiveWordIndex } from '../lib/wordAlignment';
import { findMatches, renderHighlighted, type IndexedMatch } from '../lib/highlightMatches';
import { usePlaybackRate } from '../hooks/usePlaybackRate';

export interface TranscriptPlayerProps {
  audioUrl: string;
  transcript: Transcript;
  /** Called with (segmentId, newText) when a segment edit is saved. Omit to disable editing. */
  onEditSegment?: (segmentId: string, text: string) => void;
  /** True while a segment-edit save is in flight (disables the Save button). */
  savingSegment?: boolean;
  /** Called with (label, displayName) when a speaker is renamed. Omit to disable renaming. */
  onRenameSpeaker?: (label: string, displayName: string) => void;
  /** Called with (fromLabels, intoLabel) to merge speakers. Omit to disable merging. */
  onMergeSpeakers?: (from: string[], into: string) => void;
  /** Called with (label, userId | null) to link/unlink a speaker to a directory user. Omit
   * to disable the "Link person" affordance (SHARE-6). */
  onResolveSpeaker?: (label: string, userId: string | null) => void;
  /** Directory display names for every currently-linked `Speaker.resolved_user_id`, keyed by
   * that platform user id (SHARE-6). `RecordingDetail` resolves this — this component stays
   * presentational and does no directory lookups of its own. */
  resolvedUserNames?: Map<string, string>;
  savingSpeaker?: boolean;
  /** Word-level timings for the viewed transcript version (REPLAY-2). Optional; when
   * absent, or `words_available: false`, the player is exactly today's segment-level
   * experience — this component stays presentational and fetch-free, resolved by the
   * caller (`RecordingDetail`), the same pattern as `resolvedUserNames` above. */
  words?: TranscriptWords | undefined;
  /** VOICE-1 (docs/specs/voice-1.md): "This is me", and confirming or dismissing the voice
   * matcher's suggestions. Omit when the feature is off. Confirming a suggestion goes through
   * `onResolveSpeaker`; the suggested person's name comes from `resolvedUserNames`. */
  voicePrint?: VoicePrintControls | undefined;
}

export interface VoicePrintControls {
  /** The signed-in user's platform id: "This is me" shows only on speakers that are unlinked
   * or already linked to them. */
  myUserId: string;
  onUseMyVoice: (label: string) => void;
  onDismissSuggestion: (label: string) => void;
  busy?: boolean | undefined;
  error?: string | null | undefined;
}

export function TranscriptPlayer({
  audioUrl,
  transcript,
  onEditSegment,
  savingSegment,
  onRenameSpeaker,
  onMergeSpeakers,
  onResolveSpeaker,
  resolvedUserNames,
  savingSpeaker,
  words,
  voicePrint,
}: TranscriptPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { rate: playbackRate, setRate: setPlaybackRate, bindAudio } = usePlaybackRate();
  const segmentRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [currentMs, setCurrentMs] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftText, setDraftText] = useState('');
  const [showSpeakers, setShowSpeakers] = useState(false);

  // FIND (web-ui-issues #26): in-transcript search. Deliberately NOT bound to ⌘F (that stays
  // native browser find, per the task) and NOT ⌘K (AppShell's global command-palette shortcut
  // — see its "Global ⌘K / Ctrl+K shortcut" handler; that one matches only a bare `k`, this one
  // requires `shiftKey` + `f`, so the two never collide). The toggle button next to the
  // playback controls is the primary, always-visible affordance; ⌘⇧F is a bonus accelerator.
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const findInputRef = useRef<HTMLInputElement | null>(null);

  // Display-name precedence (applied everywhere a speaker is rendered, SHARE-6 decision 2):
  // a manual rename always wins; otherwise a linked directory user's name; otherwise the
  // formatted spk_N fallback. Linking never writes `display_name` — the two fields stay
  // independent, so unlinking cleanly falls back to the prior name.
  const speakerNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const sp of transcript.speakers ?? []) {
      map.set(
        sp.label,
        sp.display_name ??
          (sp.resolved_user_id ? resolvedUserNames?.get(sp.resolved_user_id) : undefined) ??
          formatSpeaker(sp.label),
      );
    }
    return map;
  }, [transcript.speakers, resolvedUserNames]);

  // Every distinct speaker_label actually appearing in segments — not just resolved
  // `Speaker` rows — so a diarized label with no display name yet still shows up in the
  // rename/merge panel.
  const speakerLabels = useMemo(() => {
    const labels = new Set<string>();
    for (const seg of transcript.segments) labels.add(seg.speaker_label);
    return Array.from(labels).sort();
  }, [transcript.segments]);

  const activeSegmentId = useMemo(() => {
    const active = transcript.segments.find((s) => currentMs >= s.start_ms && currentMs < s.end_ms);
    return active?.id ?? null;
  }, [currentMs, transcript.segments]);

  // REPLAY-2: karaoke word alignment, computed once per (words, segments) change — see
  // wordAlignment.ts for the alignment/fallback rules. `activeWordIndex` is only ever
  // computed for the currently active segment (binary search over that segment's words),
  // never all segments — the perf contract SegmentRow below relies on.
  const wordsBySegment = useMemo(
    () => alignWordsToSegments(words, transcript.segments),
    [words, transcript.segments],
  );
  const activeWords = activeSegmentId ? wordsBySegment.get(activeSegmentId) : undefined;
  const activeWordIndex = useMemo(
    () => (activeWords ? findActiveWordIndex(activeWords, currentMs) : -1),
    [activeWords, currentMs],
  );

  // FIND: every match across the whole transcript, in segment order — this flat list's
  // position is each match's `globalIndex` (see highlightMatches.ts), which is what "current
  // match", "N of M", and scroll-to-current all key off of. Matching runs against
  // `seg.text` — the same canonical string wordAlignment.ts aligns words against — never
  // against word spans directly (see highlightMatches.ts's file header for why).
  const findMatchList = useMemo(() => {
    const flat: { segmentId: string; start: number; end: number }[] = [];
    for (const seg of transcript.segments) {
      for (const m of findMatches(seg.text, findQuery)) {
        flat.push({ segmentId: seg.id, start: m.start, end: m.end });
      }
    }
    return flat;
  }, [transcript.segments, findQuery]);

  const findMatchesBySegment = useMemo(() => {
    const map = new Map<string, IndexedMatch[]>();
    findMatchList.forEach((m, globalIndex) => {
      const arr = map.get(m.segmentId) ?? [];
      arr.push({ start: m.start, end: m.end, globalIndex });
      map.set(m.segmentId, arr);
    });
    return map;
  }, [findMatchList]);

  const findMatchCount = findMatchList.length;
  // Clamped into [0, count) with wraparound-safe modulo (handles a stale index after the
  // match list shrinks, e.g. a segment edit removes a previous match) — `-1` means "no
  // current match" when there are none at all.
  const currentFindGlobalIndex =
    findMatchCount > 0 ? ((currentMatchIndex % findMatchCount) + findMatchCount) % findMatchCount : -1;

  // Query changes (typing, or clearing) always jump back to the first match.
  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [findQuery]);

  // Scroll the current match's segment into view — reuses the same `segmentRefs` map the
  // playback auto-scroll effect below uses, just keyed off search navigation instead of
  // `currentMs`.
  useEffect(() => {
    if (currentFindGlobalIndex < 0) return;
    const current = findMatchList[currentFindGlobalIndex];
    if (!current) return;
    const el = segmentRefs.current.get(current.segmentId);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentFindGlobalIndex, findMatchList]);

  useEffect(() => {
    if (findOpen) findInputRef.current?.focus();
  }, [findOpen]);

  // Global ⌘⇧F / Ctrl+Shift+F — opens (or refocuses) the find bar from anywhere while this
  // player is mounted. Requires `shiftKey`, so it can never fire on AppShell's bare ⌘K, and
  // it's a different key than native ⌘F, which stays untouched.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        setFindOpen(true);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  function goToNextMatch() {
    setCurrentMatchIndex((i) => (findMatchCount > 0 ? (i + 1) % findMatchCount : 0));
  }

  function goToPrevMatch() {
    setCurrentMatchIndex((i) => (findMatchCount > 0 ? (i - 1 + findMatchCount) % findMatchCount : 0));
  }

  function onFindInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) goToPrevMatch();
      else goToNextMatch();
    } else if (e.key === 'Escape') {
      // ESC clears: wipes the query (so highlights disappear) and closes the find bar in
      // one step, same as dismissing any other transient overlay in this app.
      e.preventDefault();
      setFindQuery('');
      setFindOpen(false);
    }
  }

  function closeFind() {
    setFindOpen(false);
    setFindQuery('');
  }

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => setCurrentMs(audio.currentTime * 1000);
    audio.addEventListener('timeupdate', onTimeUpdate);
    return () => audio.removeEventListener('timeupdate', onTimeUpdate);
  }, []);

  useEffect(() => {
    // Also stands down while a find query is active, so playback doesn't yank the scroll
    // position away from the match the user just navigated to.
    if (!autoScroll || !activeSegmentId || editingId || findQuery.trim()) return;
    const el = segmentRefs.current.get(activeSegmentId);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeSegmentId, autoScroll, editingId, findQuery]);

  // Wrapped in useCallback with stable (empty) deps — they touch refs/setters only — so
  // React.memo on SegmentRow below actually holds across renders (REPLAY-2 perf contract).
  const seekTo = useCallback((startMs: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = startMs / 1000;
    audio.play().catch(() => {
      /* autoplay may be blocked; user can press play manually */
    });
  }, []);

  const startEdit = useCallback((segmentId: string, text: string) => {
    setEditingId(segmentId);
    setDraftText(text);
  }, []);

  const registerRef = useCallback((segmentId: string, el: HTMLDivElement | null) => {
    if (el) segmentRefs.current.set(segmentId, el);
    else segmentRefs.current.delete(segmentId);
  }, []);

  function cancelEdit() {
    setEditingId(null);
    setDraftText('');
  }

  function saveEdit(segmentId: string) {
    if (!onEditSegment) return;
    onEditSegment(segmentId, draftText);
    setEditingId(null);
  }

  return (
    <div className="transcript-player">
      <div className="audio-bar">
        <audio
          ref={(el) => {
            audioRef.current = el;
            bindAudio(el);
          }}
          src={audioUrl}
          controls
          style={{ width: '100%' }}
        />
        <div className="audio-bar-controls">
          <label className="autoscroll-toggle">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
            />
            Auto-scroll
          </label>
          <PlaybackSpeedControl rate={playbackRate} onChange={setPlaybackRate} />
          <button
            type="button"
            className="ghost sm"
            aria-expanded={findOpen}
            aria-controls="transcript-find-bar"
            onClick={() => (findOpen ? closeFind() : setFindOpen(true))}
          >
            <MagnifyingGlassIcon width={16} height={16} aria-hidden="true" />
            Find in transcript
            <span className="kbd" aria-hidden="true">
              ⌘⇧F
            </span>
          </button>
          {(onRenameSpeaker || onMergeSpeakers || onResolveSpeaker) && (
            <button
              type="button"
              className="ghost sm"
              onClick={() => setShowSpeakers((v) => !v)}
              aria-expanded={showSpeakers}
            >
              {showSpeakers ? 'Hide speakers' : 'Manage speakers'}
            </button>
          )}
        </div>
        {findOpen && (
          <div className="find-bar" id="transcript-find-bar">
            <MagnifyingGlassIcon width={16} height={16} aria-hidden="true" />
            <input
              ref={findInputRef}
              type="text"
              value={findQuery}
              onChange={(e) => setFindQuery(e.target.value)}
              onKeyDown={onFindInputKeyDown}
              placeholder="Find in transcript…"
              aria-label="Find in transcript"
            />
            <span className="find-count" aria-live="polite">
              {findQuery.trim() === ''
                ? ''
                : findMatchCount > 0
                  ? `${currentFindGlobalIndex + 1} of ${findMatchCount}`
                  : 'No matches'}
            </span>
            <button
              type="button"
              className="icon-btn"
              aria-label="Previous match"
              disabled={findMatchCount === 0}
              onClick={goToPrevMatch}
            >
              <ChevronUpIcon width={16} height={16} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label="Next match"
              disabled={findMatchCount === 0}
              onClick={goToNextMatch}
            >
              <ChevronDownIcon width={16} height={16} aria-hidden="true" />
            </button>
            <button type="button" className="icon-btn" aria-label="Close find" onClick={closeFind}>
              <XMarkIcon width={16} height={16} aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      {showSpeakers && (onRenameSpeaker || onMergeSpeakers || onResolveSpeaker) && (
        <SpeakerPanel
          labels={speakerLabels}
          speakers={transcript.speakers ?? []}
          onRename={onRenameSpeaker}
          onMerge={onMergeSpeakers}
          onResolve={onResolveSpeaker}
          resolvedUserNames={resolvedUserNames}
          saving={savingSpeaker}
          voicePrint={voicePrint}
        />
      )}

      <div className="transcript-body">
        {transcript.segments.map((seg) => {
          const active = seg.id === activeSegmentId;
          const isEditing = editingId === seg.id;
          return (
            <SegmentRow
              key={seg.id}
              seg={seg}
              active={active}
              activeWordIndex={active ? activeWordIndex : -1}
              words={wordsBySegment.get(seg.id)}
              findMatches={findMatchesBySegment.get(seg.id)}
              currentFindGlobalIndex={currentFindGlobalIndex}
              isEditing={isEditing}
              speakerName={speakerNames.get(seg.speaker_label) ?? formatSpeaker(seg.speaker_label)}
              canEdit={!!onEditSegment}
              onStartEdit={startEdit}
              onSeek={seekTo}
              registerRef={registerRef}
              draftText={isEditing ? draftText : undefined}
              setDraftText={isEditing ? setDraftText : undefined}
              onSave={isEditing ? () => saveEdit(seg.id) : undefined}
              onCancel={isEditing ? cancelEdit : undefined}
              savingSegment={savingSegment}
            />
          );
        })}
        {transcript.segments.length === 0 && <p className="muted">No transcript segments yet.</p>}
      </div>
    </div>
  );
}

interface SegmentRowProps {
  seg: Segment;
  active: boolean;
  /** -1 unless this row is the active segment — only the active row's word index is
   * ever meaningful. */
  activeWordIndex: number;
  /** This segment's aligned word timings, or undefined for a words-less version or an
   * edited/mismatched segment (REPLAY-2 fallback: renders `seg.text` as plain text). */
  words: WordTiming[] | undefined;
  /** FIND: this segment's matches (in `seg.text` character coordinates), or undefined when
   * there are none — undefined (not `[]`) lets `renderHighlighted` skip highlighting work
   * entirely for the vast majority of segments while a query is active. */
  findMatches: IndexedMatch[] | undefined;
  /** The globalIndex of the one match that's currently "current" across the whole
   * transcript, or -1 if none — same value passed to every row; only the row(s) actually
   * containing that match render it with the extra `find-match-current` emphasis. */
  currentFindGlobalIndex: number;
  isEditing: boolean;
  speakerName: string;
  canEdit: boolean;
  onStartEdit: (segmentId: string, text: string) => void;
  onSeek: (startMs: number) => void;
  registerRef: (segmentId: string, el: HTMLDivElement | null) => void;
  draftText?: string | undefined;
  setDraftText?: ((text: string) => void) | undefined;
  onSave?: (() => void) | undefined;
  onCancel?: (() => void) | undefined;
  savingSegment?: boolean | undefined;
}

/** REPLAY-2 perf contract: for a given segment, the ONLY props that change during
 * playback are `active` and `activeWordIndex` — a `timeupdate` tick re-renders exactly the
 * outgoing and incoming active rows (this memo boundary), never the other segment rows,
 * and never re-renders all ~10k word spans across the transcript at once. */
const SegmentRow = memo(function SegmentRow({
  seg,
  active,
  activeWordIndex,
  words,
  findMatches,
  currentFindGlobalIndex,
  isEditing,
  speakerName,
  canEdit,
  onStartEdit,
  onSeek,
  registerRef,
  draftText,
  setDraftText,
  onSave,
  onCancel,
  savingSegment,
}: SegmentRowProps) {
  // FIND: each word's start offset within `seg.text` — valid because `words` only exists
  // (see wordAlignment.ts) when `words.map(w => w.text).join(' ') === seg.text` exactly, so
  // offsets are just a running sum of word length + 1 (the joining space). Lets match ranges
  // computed against the canonical `seg.text` be reprojected onto each word span below.
  const wordOffsets = useMemo(() => {
    if (!words) return [];
    const offsets: number[] = [];
    let pos = 0;
    for (const w of words) {
      offsets.push(pos);
      pos += w.text.length + 1;
    }
    return offsets;
  }, [words]);

  return (
    <div
      ref={(el) => registerRef(seg.id, el)}
      className={'segment' + (active ? ' segment-active' : '')}
    >
      <div className="segment-meta">
        <Avatar name={speakerName} size="sm" />
        <span className="segment-speaker" style={{ color: speakerColor(speakerName) }}>
          {speakerName}
        </span>
        <span
          className="segment-time"
          role="button"
          tabIndex={0}
          aria-label={`Seek to ${formatMs(seg.start_ms)}`}
          onClick={() => onSeek(seg.start_ms)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSeek(seg.start_ms);
            }
          }}
        >
          {formatMs(seg.start_ms)}
        </span>
        {seg.edited_at && <span className="segment-edited-badge">edited</span>}
        {canEdit && !isEditing && (
          <button
            type="button"
            className="ghost sm segment-edit-btn"
            onClick={() => onStartEdit(seg.id, seg.text)}
          >
            Edit
          </button>
        )}
      </div>
      {isEditing ? (
        <div className="segment-edit-form">
          <textarea
            className="segment-edit-textarea"
            value={draftText ?? ''}
            onChange={(e) => setDraftText?.(e.target.value)}
            autoFocus
          />
          <div className="row">
            <button
              type="button"
              className="sm"
              disabled={savingSegment || (draftText ?? '').trim().length === 0}
              onClick={() => onSave?.()}
            >
              {savingSegment ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="ghost sm" onClick={() => onCancel?.()}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p
          className="segment-text"
          onClick={() => onSeek(seg.start_ms)}
          role="button"
          tabIndex={0}
          aria-label={`Seek to ${formatMs(seg.start_ms)}: ${seg.text}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSeek(seg.start_ms);
            }
          }}
        >
          {words
            ? words.map((w, i) => (
                <span
                  key={i}
                  className={'word' + (i === activeWordIndex ? ' word-active' : '')}
                  onClick={(e) => {
                    e.stopPropagation(); // word seek wins over the paragraph's segment seek
                    onSeek(w.start_ms);
                  }}
                >
                  {renderHighlighted(w.text, wordOffsets[i] ?? 0, findMatches, currentFindGlobalIndex)}{' '}
                </span>
              ))
            : renderHighlighted(seg.text, 0, findMatches, currentFindGlobalIndex)}
        </p>
      )}
    </div>
  );
});

function SpeakerPanel({
  labels,
  speakers,
  onRename,
  onMerge,
  onResolve,
  resolvedUserNames,
  saving,
  voicePrint,
}: {
  labels: string[];
  speakers: Speaker[];
  onRename?: ((label: string, displayName: string) => void) | undefined;
  onMerge?: ((from: string[], into: string) => void) | undefined;
  /** Called with (label, userId | null) to link/unlink a speaker (SHARE-6). */
  onResolve?: ((label: string, userId: string | null) => void) | undefined;
  resolvedUserNames?: Map<string, string> | undefined;
  saving?: boolean | undefined;
  voicePrint?: VoicePrintControls | undefined;
}) {
  // VOICE-1: the speaker whose "This is me" consent step is open (one at a time).
  const [consentLabel, setConsentLabel] = useState<string | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [mergeFrom, setMergeFrom] = useState<Set<string>>(new Set());
  const [mergeInto, setMergeInto] = useState<string>(labels[0] ?? '');
  // Only one row's "Link person" picker open at a time (SHARE-6 decision).
  const [linkingLabel, setLinkingLabel] = useState<string | null>(null);

  function linkedNameFor(label: string): string | undefined {
    const resolvedId = speakerFor(speakers, label)?.resolved_user_id;
    return resolvedId ? resolvedUserNames?.get(resolvedId) : undefined;
  }

  // SHARE-6 decision 2: same display-name precedence as `speakerNames` above — a manual
  // rename wins, then a linked directory user's name, then the formatted fallback.
  const displayNameFor = (label: string) =>
    names[label] ??
    speakers.find((s) => s.label === label)?.display_name ??
    linkedNameFor(label) ??
    '';

  function toggleMergeFrom(label: string) {
    setMergeFrom((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }

  function submitMerge() {
    if (!onMerge) return;
    const from = Array.from(mergeFrom).filter((l) => l !== mergeInto);
    if (from.length === 0 || !mergeInto) return;
    onMerge(from, mergeInto);
    setMergeFrom(new Set());
  }

  return (
    <div className="speaker-panel">
      <h3 className="subhead">Speakers</h3>
      <div className="speaker-rows">
        {labels.map((label) => {
          const shownName = displayNameFor(label) || formatSpeaker(label);
          const speaker = speakerFor(speakers, label);
          const resolvedUserId = speaker?.resolved_user_id ?? null;
          const isLinking = linkingLabel === label;
          const suggestedUserId =
            voicePrint && resolvedUserId == null ? (speaker?.suggested_user_id ?? null) : null;
          const canClaim =
            !!voicePrint && (resolvedUserId == null || resolvedUserId === voicePrint.myUserId);
          return (
            <div key={label}>
              <div className="speaker-row">
                <Avatar name={shownName} size="sm" />
                <span className="segment-speaker" style={{ color: speakerColor(shownName) }}>
                  {shownName}
                </span>
                {onRename && (
                  <>
                    <input
                      className="speaker-name-input"
                      value={displayNameFor(label)}
                      placeholder="Display name"
                      onChange={(e) => setNames((prev) => ({ ...prev, [label]: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="ghost sm"
                      disabled={saving || !displayNameFor(label).trim()}
                      onClick={() => onRename(label, displayNameFor(label).trim())}
                    >
                      {saving ? 'Saving…' : 'Rename'}
                    </button>
                  </>
                )}
                {onMerge && (
                  <label className="merge-checkbox">
                    <input
                      type="checkbox"
                      checked={mergeFrom.has(label)}
                      onChange={() => toggleMergeFrom(label)}
                    />
                    merge
                  </label>
                )}
                {onResolve &&
                  (resolvedUserId == null ? (
                    <button
                      type="button"
                      className="ghost sm"
                      disabled={saving}
                      aria-expanded={isLinking}
                      onClick={() => setLinkingLabel(isLinking ? null : label)}
                    >
                      Link person
                    </button>
                  ) : (
                    <>
                      <span className="muted">
                        Linked to {resolvedUserNames?.get(resolvedUserId) ?? '…'}
                      </span>
                      <button
                        type="button"
                        className="ghost sm"
                        disabled={saving}
                        onClick={() => onResolve(label, null)}
                      >
                        Unlink
                      </button>
                    </>
                  ))}
                {speaker?.name_source === 'voice_print' && (
                  <span className="muted voice-named" title="Named automatically from a voice print">
                    by voice
                  </span>
                )}
                {canClaim && (
                  <button
                    type="button"
                    className="ghost sm"
                    disabled={voicePrint?.busy}
                    aria-expanded={consentLabel === label}
                    onClick={() => setConsentLabel(consentLabel === label ? null : label)}
                  >
                    This is me
                  </button>
                )}
              </div>
              {suggestedUserId && onResolve && voicePrint && (
                <div className="voice-suggestion row" role="group" aria-label={`Suggestion for ${shownName}`}>
                  <span>Is this {resolvedUserNames?.get(suggestedUserId) ?? 'someone you know'}?</span>
                  <button
                    type="button"
                    className="sm"
                    disabled={saving}
                    onClick={() => onResolve(label, suggestedUserId)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className="ghost sm"
                    disabled={saving}
                    onClick={() => voicePrint.onDismissSuggestion(label)}
                  >
                    No
                  </button>
                </div>
              )}
              {voicePrint && consentLabel === label && (
                <VoicePrintConsent
                  speakerName={shownName}
                  busy={voicePrint.busy}
                  error={voicePrint.error}
                  onConfirm={() => {
                    voicePrint.onUseMyVoice(label);
                    setConsentLabel(null);
                  }}
                  onCancel={() => setConsentLabel(null)}
                />
              )}
              {onResolve && isLinking && (
                <div className="speaker-link-picker">
                  <UserPicker
                    onSelect={(u) => {
                      onResolve(label, u.id);
                      setLinkingLabel(null);
                    }}
                    ariaLabel={`Link ${shownName} to a person`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {onMerge && labels.length > 1 && (
        <div className="merge-controls row">
          <span className="muted">Merge selected into</span>
          <select value={mergeInto} onChange={(e) => setMergeInto(e.target.value)}>
            {labels.map((label) => (
              <option key={label} value={label}>
                {speakerFor(speakers, label)?.display_name ?? label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="sm"
            disabled={saving || mergeFrom.size === 0}
            onClick={submitMerge}
          >
            {saving ? 'Merging…' : 'Merge'}
          </button>
        </div>
      )}
    </div>
  );
}

function speakerFor(speakers: Speaker[], label: string): Speaker | undefined {
  return speakers.find((s) => s.label === label);
}
